import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { api, formatApiErrorDetail } from "@/lib/api";

const resolveReturnHintLabel = (nextPath = "") => {
  if (!nextPath) return null;
  if (nextPath.includes("/founding-checkout") || nextPath.includes("/checkout")) return "checkout";
  if (nextPath.includes("/billing")) return "billing";
  if (nextPath.includes("/onboarding-wizard")) return "onboarding";
  if (nextPath.includes("/dashboard")) return "dashboard";
  if (nextPath.startsWith("/agent/")) return "workspace";
  return null;
};

export const AgentLoginPage = ({ authLoading, onLogin, resolveAgentPostLoginPath, resolveUserDashboardPath, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const requestedNextPath = new URLSearchParams(location.search).get("next") || "";
  const returnHintLabel = resolveReturnHintLabel(requestedNextPath);
  const showReturnHint = Boolean(returnHintLabel);
  const returnHintCopy = returnHintLabel
    ? `You’ll be returned to your ${returnHintLabel} after sign-in.`
    : "";

  useSeo({
    title: "Agent Login | SG Prop Portal",
    description: "Secure agent login for SG Prop Portal workspaces.",
  });

  useEffect(() => {
    if (!authLoading && user) {
      const nextPath = ["agent", "pending_agent"].includes(user?.role)
        ? resolveAgentPostLoginPath(user, location.search)
        : "/admin";
      navigate(nextPath, { replace: true });
    }
  }, [authLoading, location.search, navigate, resolveAgentPostLoginPath, user]);

  if (!authLoading && user) {
    const nextPath = ["agent", "pending_agent"].includes(user?.role)
      ? resolveAgentPostLoginPath(user, location.search)
      : "/admin";
    return <Navigate replace to={nextPath} />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await api.post("/auth/login", form);
      const nextUser = response.data?.user;
      onLogin(response.data);
      if (["agent", "pending_agent"].includes(nextUser?.role)) {
        toast.success("Agent workspace ready");
        navigate(resolveAgentPostLoginPath(nextUser, location.search), { replace: true });
        return;
      }
      toast.success("Admin workspace ready");
      navigate(resolveUserDashboardPath(nextUser), { replace: true });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a1830] px-6 py-10">
      <form className="luxury-panel w-full max-w-lg space-y-5 p-6 md:p-8" data-testid="agent-login-form" onSubmit={handleSubmit}>
        <p className="subtle-label">Agent Workspace Access</p>
        <h1 className="text-4xl font-semibold text-white" data-testid="agent-login-heading">
          Agent sign in
        </h1>
        <p className="text-sm leading-7 text-slate-300" data-testid="agent-login-copy">
          Sign in with your agent account to open your own branded dashboard, listings workspace, and lead tools.
        </p>
        {showReturnHint ? (
          <div className="rounded-[18px] border border-[#D4AF37]/25 bg-[#fff4db] px-4 py-3 text-sm font-medium leading-7 text-[#6a5312]" data-testid="agent-login-return-hint">
            {returnHintCopy}
          </div>
        ) : null}
        <input className="field-shell" data-testid="agent-login-email-input" onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Agent email" required type="email" value={form.email} />
        <input className="field-shell" data-testid="agent-login-password-input" onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="Password" required type="password" value={form.password} />
        <button className="gold-button w-full" data-testid="agent-login-submit-button" disabled={submitting} type="submit">
          {submitting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
          Sign In
        </button>
      </form>
    </div>
  );
};